import { FeatureProtectedView } from '@moniebook-web/components/molecules/feature-protected-view';
import { ProtectedView } from '@moniebook-web/components/molecules/protected-view';
import type { RouteObject } from 'react-router';

import { StyleScope } from '@/features/shared/style-scope';
import { FEATURE_FLAGS, LOYALTY_PAGE_PERMISSIONS } from '@/shared/constants';

const WALLET_BULK_UPLOAD_FLAG = 'moniebook.pos.walletBulkUpload';

const CUSTOMERS_PAGE_PERMISSIONS = [
    'VIEW_CUSTOMER',
    'MANAGE_CUSTOMER',
    'EXPORT_CUSTOMERS_LIST',
];
const DISCOUNTS_PAGE_PERMISSIONS = ['VIEW_DISCOUNT', 'MANAGE_DISCOUNT'];

export const customerRoutes: RouteObject[] = [
    {
        path: '',
        element: <StyleScope name='customers-ui' />,
        children: [
            {
                path: 'customers',
                element: (
                    <ProtectedView
                        accessCodeList={CUSTOMERS_PAGE_PERMISSIONS}
                        grantAccessIfAny
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { customerList } = await unwrapFeatures();
                            return customerList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'groups',
                        async lazy() {
                            const { groupTab } = await unwrapFeatures();
                            return groupTab().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'groups/:groupId',
                        element: (
                            <ProtectedView
                                accessCodeList={['VIEW_CUSTOMER']}
                                grantAccessIfAny
                            />
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { groupDetails } =
                                        await unwrapFeatures();
                                    return groupDetails().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: ':id',
                        element: (
                            <FeatureProtectedView
                                flag={
                                    FEATURE_FLAGS.ENABLE_NEW_DESIGN_SYSTEM_CRM
                                }>
                                <ProtectedView
                                    accessCodeList={['VIEW_CUSTOMER']}
                                    grantAccessIfAny
                                />
                            </FeatureProtectedView>
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { customerDetailsPage } =
                                        await unwrapFeatures();
                                    return customerDetailsPage().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView
                                accessCodeList={['MANAGE_CUSTOMER']}
                            />
                        ),
                        children: [
                            {
                                path: 'create-customer',
                                async lazy() {
                                    const { createCustomer } =
                                        await unwrapFeatures();
                                    return createCustomer().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                            {
                                path: ':id/edit',
                                async lazy() {
                                    const { updateCustomer } =
                                        await unwrapFeatures();
                                    return updateCustomer().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                            {
                                path: 'create-group',
                                async lazy() {
                                    const { createGroup } =
                                        await unwrapFeatures();
                                    return createGroup().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                            {
                                path: 'groups/:id/edit',
                                async lazy() {
                                    const { updateGroup } =
                                        await unwrapFeatures();
                                    return updateGroup().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'discounts',
                element: (
                    <ProtectedView
                        accessCodeList={DISCOUNTS_PAGE_PERMISSIONS}
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { discountList } = await unwrapFeatures();
                            return discountList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView
                                accessCodeList={['MANAGE_DISCOUNT']}
                            />
                        ),
                        children: [
                            {
                                path: 'create',
                                async lazy() {
                                    const { createDiscount } =
                                        await unwrapFeatures();
                                    return createDiscount().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                            {
                                path: ':id/edit',
                                async lazy() {
                                    const { updateDiscount } =
                                        await unwrapFeatures();
                                    return updateDiscount().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'loyalty',
                element: (
                    <FeatureProtectedView flag={FEATURE_FLAGS.ENABLE_LOYALTY}>
                        <ProtectedView
                            accessCodeList={LOYALTY_PAGE_PERMISSIONS}
                            grantAccessIfAny
                        />
                    </FeatureProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { loyaltyList } = await unwrapFeatures();
                            return loyaltyList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView
                                accessCodeList={['MANAGE_LOYALTY_PROGRAM']}
                            />
                        ),
                        children: [
                            {
                                path: 'create',
                                async lazy() {
                                    const { createLoyaltyProgram } =
                                        await unwrapFeatures();
                                    return createLoyaltyProgram().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                            {
                                path: ':id/edit',
                                async lazy() {
                                    const { updateLoyaltyProgram } =
                                        await unwrapFeatures();
                                    return updateLoyaltyProgram().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'wallet-payment',
                element: (
                    <FeatureProtectedView flag={WALLET_BULK_UPLOAD_FLAG}>
                        <ProtectedView
                            accessCodeList={['FUND_WALLET']}
                            grantAccessIfAny
                        />
                    </FeatureProtectedView>
                ),
                children: [
                    {
                        path: 'import',
                        async lazy() {
                            const { walletPaymentImport } =
                                await unwrapFeatures();
                            return walletPaymentImport().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: ':importId/status',
                        async lazy() {
                            const { walletImportStatus } =
                                await unwrapFeatures();
                            return walletImportStatus().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
        ],
    },
];

async function unwrapFeatures() {
    const features = await import('customers/features');

    return features.default;
}
